import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { classMap } from 'lit/directives/class-map.js';
import { sharedStyles } from '../../../util/shared-styles';
import { breakpointContext } from '../../../contexts/breakpoint';
import { BREAKPOINT } from '../../../util/breakpoints';
interface PlanDetail {
    label: string;
    inNetwork: string | undefined;
    outOfNetwork: string | undefined;
}

@customElement('rq-plan-details-card')
export class PlanDetailsCard extends LitElement {
    @consume({ context: breakpointContext, subscribe: true })
    breakpoint!: BREAKPOINT;

    @property({ type: String })
    title = '';

    @property({ type: String })
    icon = '';

    @property({ type: String })
    description = '';

    @property({ type: Array })
    data: PlanDetail[] = [];

    static styles = [
        sharedStyles,
        css`
            .card {
                margin-top: 1.5rem;
            }
            table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 1rem;
            }
            th,
            td {
                border-bottom: 1px solid var(--lh-color-neutral-200, #d1d3d4);
                padding: 0.75rem;
                text-align: left;
                width: 36%;
            }
            th {
                background-color: var(--lh-color-primary-50, #ebf9ff);
                font-weight: bold;
            }
            td {
                background-color: var(--lh-color-neutral-50, #f9f9f9);
            }
            tbody tr:nth-child(odd) td {
                background-color: var(--lh-color-neutral-50, #f9f9fa);
            }
            tbody tr:nth-child(even) td {
                background-color: var(--lh-color-neutral-100, #ffffff);
            }
            .icon {
                display: flex;
                align-items: center;
                gap: 0.5rem;
            }
            .description {
                margin-top: 0.5rem;
                color: var(--lh-color-neutral-700, #4a4a4a);
            }
            .sub-header-wrapper {
                display: flex;
                justify-content: flex-start;
                gap: 0.8rem;
            }
            .sub-header {
                width: 70%;
                margin-top: 0.75rem;
            }
        `,
    ];

    get isMobile() {
        return this.breakpoint === BREAKPOINT.PHONE || this.breakpoint === BREAKPOINT.TABLET;
    }

    render() {
        return html`
            <div class="card">
                <div
                    class=${classMap({
                        'align-items-start': this.description,
                        'align-items-end': !this.description,
                        'sub-header-wrapper': true,
                    })}
                >
                    <div>
                        <span style="color: var(--lh-color-primary-900)">
                            <lh-icon name="${this.icon}" size="xxl"></lh-icon>
                        </span>
                    </div>
                    <div>
                        <lh-typography type="heading" selector="h6" size="xl"> ${this.title} </lh-typography>
                        <p class=${classMap({ 'sub-header': !this.isMobile, '': this.isMobile })}>
                            ${this.description}
                        </p>
                    </div>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th scope="col">
                                <lh-typography></lh-typography>
                            </th>
                            <th scope="col">
                                <lh-typography>In-network</lh-typography>
                            </th>
                            <th scope="col">
                                <lh-typography>Out-of-network</lh-typography>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this.data.map(
                            (row) => html`
                                <tr>
                                    <td>
                                        <lh-typography>${row.label}</lh-typography>
                                    </td>
                                    <td>
                                        <lh-typography>${row.inNetwork}</lh-typography>
                                    </td>
                                    <td>
                                        <lh-typography>
                                            ${row.label === 'Urgent care' || row.label === 'Emergency room'
                                                ? html`${row.outOfNetwork}<sup>1</sup>`
                                                : row.outOfNetwork}</lh-typography
                                        >
                                    </td>
                                </tr>
                            `
                        )}
                    </tbody>
                </table>
            </div>
        `;
    }
}
declare global {
    interface HTMLElementTagNameMap {
        'rq-plan-details-card': PlanDetailsCard;
    }
}
